/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */

import React, { useContext, useEffect, useRef, useState } from "react"
import { useHistory } from 'react-router-dom';

import { Form, Formik } from 'formik';

import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { MessageCenter } from '@fitinsur/utils';

import { useGetOperationAsync } from '@fitinsur/services/OperationService';
import { useGetQuestionnairesByIdProduct } from '@fitinsur/services/QuestionnairesService';
import { useUpdateAdminAccessGroupOperation } from '@fitinsur/services/AdminService';
import { useGetAccessGroupById } from '@fitinsur/services/AccessGroupService';

import {
  FormSectionGlobal as FormSection,
  FormContainer,
  HasPermission,
  Button,
  ButtonWithIcon,
  ButtonContainer,
  Row,
  Col,
} from '@fitinsur/components'

import Layout from 'components/Layout';

import {
  generalHandler,
  accessGroupProfileHandler,
  accessGroupUserHandler,
  accessGroupHandler
} from "./sectionHandlers/index";

export default function ManagementEditAccessGroup({ permissions, match, ...props }) {
  const history = useHistory();

  const formikRef = useRef();
  const getQuestionnaire = useRef(false);
  const sentUpdateAdminOperation = useRef(false);

  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const { getDescription } = useTextMessage();

  const mc = new MessageCenter();

  const [forceLoading, setForceLoading] = useState(false);
  const [operation, setOperation] = useState(null);
  const [accessGroupData, setAccessGroupData] = useState(null);
  const [rolesGroup, setRolesGroup] = useState([]);
  const [usersGroup, setUsersGroup] = useState([]);
  const [isBound, setIsBound] = useState(location.pathname.includes('view'));
  const [questionnaire, setQuestionnaire] = useState([]);
  const [isExternal, setIsExternal] = useState(false);
  const SECTIONS = {
    ACCESS_GROUP: { name: 'ACCESS_GROUP', isMultiple: false, isRisk: false },
    ACCESS_GROUP_PROFILE: { name: 'ACCESS_GROUP_PROFILE', isMultiple: false, isRisk: false },
    ACCESS_GROUP_USER: { name: 'ACCESS_GROUP_USER', isMultiple: false, isRisk: false },
  };

  const {
    loading: useGetOperationLoading,
    request: useGetOperationRequest
  } = useGetOperationAsync(match.params.idOperation);

  const {
    data: useGetQuestionnairesByIdProductData,
    loading: useGetQuestionnairesByIdProductLoading,
    status: useGetQuestionnairesByIdProductStatus,
    request: useGetQuestionnairesByIdProductRequest,
  } = useGetQuestionnairesByIdProduct(match.params.idProduct);

  const {
    loading: useGetAccessGroupByIdLoading,
    request: useGetAccessGroupByIdRequest,
  } = useGetAccessGroupById(match.params.idAccessGroup);
  

  const {
    loading: useUpdateAdminOperationLoading,
    request: useUpdateAdminOperationRequest,
  } = useUpdateAdminAccessGroupOperation();

  async function getAccessGroupData() {
    if (!match.params.idAccessGroup) return;

    await useGetAccessGroupByIdRequest()
      .then(({status, data }) => {
        if ( data.statusCode !== 200 || status !== 200 ){
          // ToDo add textMessage
          mc.error('Não foi possivel carregar as informações do Grupo, tente novamente mais tarde.');
          history.push(`/management/58/access-group`);
          return;
        } 
          const groupRoles = data.value.accessGroupMemberships.filter((grpMembership)=> grpMembership.idDomain === 307)
          const groupUsers = data.value.accessGroupMemberships.filter((grpMembership)=> grpMembership.idDomain === 306)

          setAccessGroupData(data.value);
          setRolesGroup(groupRoles);
          setUsersGroup(groupUsers);
        
        getQuestionnaireData();
      });
  }

  async function getQuestionnaireData() {
    if (getQuestionnaire.current) return;
    getQuestionnaire.current = true

    if (match.params.idOperation) {
      useGetOperationRequest(null, { loadLastDocument: true })
        .then((resp) => {
          if (resp.status === 200) {
            const { data: { value } } = resp;

            setOperation(value);
          }
        });
    }

    if (match.params.idAccessGroup) {
      useGetQuestionnairesByIdProductRequest(null, { idDomain: 752, idDomainStatus: 452 });
    } else {
      useGetQuestionnairesByIdProductRequest(null, { idDomain: 752, idDomainStatus: 451 });
    }
  }

  useEffect(() => {
    getAccessGroupData();
    getQuestionnaireData();
  }, []);

  useEffect(() => {
    if (useGetQuestionnairesByIdProductStatus === 200 &&
      ((match.params.idAccessGroup && accessGroupData) || !match.params.idAccessGroup)
    ) {
      generalHandler.events.handleQuestionnaire({
        data: useGetQuestionnairesByIdProductData?.value,
        setQuestionnaire,
        setFieldValue: formikRef?.current?.setFieldValue,
        accessGroupData,
        setRolesGroup,
        setUsersGroup,
        idAccessGroup: match.params.idAccessGroup,
        SECTIONS,
        setIsExternal
      });
    }
  }, [
    useGetQuestionnairesByIdProductStatus,
    accessGroupData
  ])

  useEffect(() => {
    updateIsLoadingStatus(
      forceLoading ||
      useGetOperationLoading ||
      useGetQuestionnairesByIdProductLoading ||
      useUpdateAdminOperationLoading ||
      useGetAccessGroupByIdLoading
    )
  }, [
    forceLoading,
    useGetOperationLoading,
    useGetQuestionnairesByIdProductLoading,
    useUpdateAdminOperationLoading, 
    useGetAccessGroupByIdLoading
  ]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{}}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      {({ values, setFieldValue, errors, setFieldError }) => (
        <Layout
          idOperation={props?.idOperation}
          title={values.fullName || getDescription("DOMAINS", 831)}>
          <Form>
            <FormContainer
              noTitlePadding
              title={getDescription("DOMAINS", 832)}
              enumerate={1}
              hide={!generalHandler.events.sectionIsVisible({
                sectionName: "ACCESS_GROUP",
                questionnaire,
                permissions,
                currentModality: null,
                ignoreVisibilityByModality: true
              })}
            >
              <FormSection
                setQuestionnaire={setQuestionnaire}
                sectionName="ACCESS_GROUP"
                disableAll={isBound}
                idProduct={match.params.idProduct}
                values={values}
                errors={errors}
                setFieldValue={setFieldValue}
                setFieldError={setFieldError}
                questionnaire={questionnaire}          
                permissions={permissions}
                isExternal={isExternal}
                onQuestionChange={(
                  question,
                  value,
                  setFieldValue,
                  questionnaireUpdated
                ) => {
                  accessGroupHandler.events.handleQuestionChange({
                    question,
                    value,
                    questionnaireUpdated,
                    setQuestionnaire,
                    setFieldValue,
                    setIsExternal,
                    idAccessGroup: match.params.idAccessGroup,
                    mc,
                    getDescription
                  })
                }}
              />
            </FormContainer>

            <FormContainer
              noTitlePadding
              title={getDescription("DOMAINS", 833)}
              enumerate={2}
              hide={!generalHandler.events.sectionIsVisible({
                sectionName: "ACCESS_GROUP_PROFILE",
                questionnaire,
                permissions,
                currentModality: null,
                ignoreVisibilityByModality: true
              })}
            >
              <Row>
                <Col style={{ paddingRight: '0px' }}>
                  <FormSection
                    setQuestionnaire={setQuestionnaire}
                    sectionName="ACCESS_GROUP_PROFILE"
                    disableAll={isBound}
                    idProduct={match.params.idProduct}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    questionnaire={questionnaire}
                    permissions={permissions}
                    isExternal={isExternal}
                    searchRequestDebounceMS={500}
                    onQuestionChange={(
                      question,
                      value,
                      setFieldValue,
                      questionnaireUpdated
                    ) => {
                      accessGroupProfileHandler.events.handleQuestionChange({
                        question,
                        value,
                        questionnaireUpdated,
                        setQuestionnaire,
                        setFieldValue,
                      })
                    }}
                  />
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center', paddingTop: '16px', paddingLeft: '0px' }}>
                  <HasPermission
                    permission={!isBound}
                  >
                    <Button
                      id="btnAddUser"
                      type="button"
                      onClick={() => accessGroupProfileHandler.events.addProfile({ rolesGroup, setRolesGroup, setFieldValue, questionnaire, mc })}
                      third
                    >
                      {getDescription("DOMAINS", 1010, { withSkeleton: true, textFormat: 'uppercase', skeletonColor: "rgba(255, 255, 255, 0.3)" })}
                    </Button>
                  </HasPermission>
                </Col>
              </Row>
              {accessGroupProfileHandler.components.RenderProfileList({
                formik:formikRef,
                listData: rolesGroup,
                setRolesGroup,
                isBound,
                getDescription,
                idGroup: match.params.idAccessGroup
              })}
            </FormContainer>
            
            <FormContainer
              noTitlePadding
              title={getDescription("DOMAINS", 834)}
              enumerate={3}
              hide={!generalHandler.events.sectionIsVisible({
                sectionName: "ACCESS_GROUP_USER",
                questionnaire,
                permissions,
                currentModality: null,
                ignoreVisibilityByModality: true
              })}
            >
              <Row>
                <Col  style={{ paddingRight: '0px' }} >
                  <FormSection
                    setQuestionnaire={setQuestionnaire}
                    sectionName="ACCESS_GROUP_USER"
                    disableAll={isBound}
                    idProduct={match.params.idProduct}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    questionnaire={questionnaire}
                    permissions={permissions}
                    isExternal={isExternal}
                    searchRequestDebounceMS={500}
                    onQuestionChange={(
                      question,
                      value,
                      setFieldValue,
                      questionnaireUpdated
                    ) => {
                      accessGroupUserHandler.events.handleQuestionChange({
                        question,
                        value,
                        questionnaireUpdated,
                        setQuestionnaire,
                        setFieldValue,
                      })
                    }}
                  />
                </Col>
                <Col style={{ display: 'flex', alignItems: 'center',  paddingTop: '16px', paddingLeft: '0px' }}>
                  <HasPermission
                    permission={!isBound}
                  >
                    <Button
                      id="btnAddUser"
                      type="button"
                      onClick={() => accessGroupUserHandler.events.addUser({ questionnaire, setFieldValue, usersGroup, setUsersGroup, mc })}
                      third
                    >
                      {getDescription("DOMAINS", 1011, { withSkeleton: true, textFormat: 'uppercase', skeletonColor: "rgba(255, 255, 255, 0.3)" })}
                    </Button>
                  </HasPermission>
                </Col>
              </Row>
              {accessGroupUserHandler.components.RenderUserList({
                formik:formikRef,
                listData: usersGroup,
                setUsersGroup,
                isBound,
                getDescription,
                idGroup: match.params.idAccessGroup
              })}
            </FormContainer>
          </Form>
          
          <ButtonContainer>
            <Button forth onClick={() => history.push(`/management/58/access-group`)}>&lt; {getDescription("DOMAINS", 758)}</Button>

            <HasPermission
              permission={!isBound}
            >
              <Button
                id="btnSave"
                type="button"
                onClick={() => generalHandler.events.handleSave({
                  values,
                  questionnaire,
                  idAccessGroup: match.params.idAccessGroup,
                  idOperation: match.params.idOperation,
                  idOperationParent: operation?.idOperationParent,
                  SECTIONS,
                  rolesGroup,
                  usersGroup,
                  isDraft: false,
                  getDescription,
                  sentUpdateAdminOperation,
                  useUpdateAdminOperationRequest,
                  mc,
                  formikRef,
                  history
                })}
                primary
              >
                {getDescription("DOMAINS", 759)}
              </Button>
            </HasPermission>
          </ButtonContainer>
        </Layout>
      )}
    </Formik >
  )
}